import React, { useCallback, useContext, useId, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import OfferCapacityWithAgesSelectBox from './OfferCapacityWithAgesSelectBox'
import { OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OfferCapacityWithAgesVerticalSelection from './OfferCapacityWithAgesVerticalSelection'
import { getMaxGuestCount } from 'lib/offer/occupancyUtils'

import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import { canBookNow, getPackagesForFilters } from 'selectors/offerPage/offerPageSelectors'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'

interface Props {
  open: boolean
  capacities?: Array<App.RoomCapacity>
  occupantUnit?: 'guest' | 'traveller'
  childrenAgeLabel?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  disableMultiRoom?: boolean
  canBookNow?: boolean
  relevantPackage: App.OfferPackageOptionView
  rooms?: Array<App.RoomOccupants>
}

const defaultRooms = [generateOfferPageRoom()]

function OfferCapacityWithAgesVerticalSelect(props: Props) {
  const {
    open,
    capacities,
    occupantUnit = 'guest',
    childrenAgeLabel = 'Age at time of check-in',
    disableMultiRoom,
    onClick,
    canBookNow,
    relevantPackage,
    rooms = defaultRooms,
  } = props
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const isMobile = useScreenSizeOnly('mobile')
  const [localRooms, setLocalRooms] = useState<Array<App.RoomOccupants | App.Occupants>>(rooms)

  const saveRooms = useCallback(() => {
    dispatch({
      type: OfferPageActions.setRooms,
      rooms: localRooms as any,
    })
  }, [dispatch, localRooms])

  const onClose = useCallback(() => {
    saveRooms()
    dispatch({ type: OfferPageActions.closeFilter })
    trackEvent('OCCUPANCY_FILTERS_CLOSED')
  }, [dispatch, saveRooms, trackEvent])

  const onContinue = useCallback(() => {
    saveRooms()
    dispatch({ type: OfferPageActions.filterContinue })
  }, [dispatch, saveRooms])

  const intl = useIntl()
  const maxGuests = useMemo((): number => getMaxGuestCount(capacities), [capacities])

  const onDropdownSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onContinue()
  }, [onContinue])

  const formId = useId()

  const whoIsGoingTitle = intl.formatMessage({
    id: 'ts2lc3WWk+',
    defaultMessage: 'Who is going?',
    description: 'Title for guest selection dropdown on offer page',
  })
  const maxOccupancySubtitle = maxGuests > 0
    ? occupantUnit === 'traveller'
      ? intl.formatMessage(
          {
            id: 'YSRuhixdEx',
            defaultMessage: 'Maximum occupancy is {maxGuests, plural, one {# traveller} other {# travellers}}',
            description: 'Subtitle showing maximum traveller occupancy for room selection',
          },
          { maxGuests },
        )
      : intl.formatMessage(
          {
            id: 'x652o2VJ+i',
            defaultMessage: 'Maximum occupancy is {maxGuests, plural, one {# guest} other {# guests}}',
            description: 'Subtitle showing maximum guest occupancy for room selection',
          },
          { maxGuests },
        )
    : undefined
  const bookNowLabel = intl.formatMessage({
    id: 'yn8DpgCo1d',
    defaultMessage: 'Book now',
    description: 'Button text to proceed to booking on offer page',
  })
  const continueLabel = intl.formatMessage({
    id: 'xIHmkInhnb',
    defaultMessage: 'Continue',
    description: 'Button text to continue to next step in offer page guest selection',
  })

  return (
    <>
      <OfferCapacityWithAgesSelectBox
        ref={triggerRef}
        occupantUnit={occupantUnit}
        onClick={onClick}
        multiRoomDisabled={disableMultiRoom}
      />
      <DropdownSheet
        size="M"
        open={open && !isMobile}
        onClose={onClose}
        anchorRef={triggerRef}
        triggerRef={triggerRef}
        data-testid="offer-capacity-with-ages-vertical-select-dropdown"
        title={whoIsGoingTitle}
        subtitle={maxOccupancySubtitle}
        footerActions={<>
          {canBookNow && <OfferBookButton
            fit="full-width"
            size="large"
            type="submit"
            form={formId}
            packageView={relevantPackage}
            overrideRooms={localRooms}
          >
            {bookNowLabel}
          </OfferBookButton>}
          {!canBookNow && <TextButton
            kind="primary"
            fit="full-width"
            size="large"
            type="submit"
            form={formId}
          >
            {continueLabel}
          </TextButton>}
        </>}
      >
        <form onSubmit={onDropdownSubmit} id={formId}>
          <OfferCapacityWithAgesVerticalSelection
            capacities={capacities}
            childrenAgeLabel={childrenAgeLabel}
            disableMultiRoom={disableMultiRoom}
            onChange={setLocalRooms}
          />
        </form>
      </DropdownSheet>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  canBookNow: canBookNow(state, appState),
  relevantPackage: getPackagesForFilters(appState, state)[0],
  open: state.activeFilter === 'capacity',
  rooms: state.rooms,
}), OfferCapacityWithAgesVerticalSelect)
